import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { axiosInstance } from '../../App';
import { 
  ChevronDown, ChevronRight, User, MapPin, Mail, Phone, 
  Calendar, Clock, DollarSign, FileText, Receipt, Briefcase, 
  Play, Pause, CheckCircle, XCircle, AlertTriangle, Upload,
  Download, Plus, Edit, Eye, EyeOff, TrendingUp, TrendingDown,
  MessageSquare, Paperclip, Timer, Map, X, Calculator,
  Trash2, Save, ShoppingCart, CreditCard, Wallet, CircleDollarSign,
  ClipboardCheck, BarChart3, Activity, Banknote, ArrowDownCircle,
  ArrowUpCircle, FileCheck, Send, Loader2, Printer, Image,
  Wrench, UserCheck, RefreshCw, Camera, Navigation
} from 'lucide-react';
import InvoiceView from './InvoiceView';
import { formatDate, formatDateTime, formatTime } from '../../utils/dateUtils';

export default function JobDetails({ jobId, open, onClose }) {
  const [loading, setLoading] = useState(true);
  const [jobData, setJobData] = useState(null);
  const [hideProfitability, setHideProfitability] = useState(false);
  const [expandedLineItems, setExpandedLineItems] = useState({});
  const [expandedExpenses, setExpandedExpenses] = useState({});
  const [expandedTimeEntries, setExpandedTimeEntries] = useState({});
  const [expandedVisits, setExpandedVisits] = useState({});
  const [expandedInvoices, setExpandedInvoices] = useState({});
  const [showAddExpense, setShowAddExpense] = useState(false);
  const [showAddNote, setShowAddNote] = useState(false);
  const [showAddTimeEntry, setShowAddTimeEntry] = useState(false);
  const [showInvoiceView, setShowInvoiceView] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [creatingInvoice, setCreatingInvoice] = useState(false);
  const [showCreateInvoiceDialog, setShowCreateInvoiceDialog] = useState(false);
  const [actualPayments, setActualPayments] = useState(0);
  const [advanceAmount, setAdvanceAmount] = useState(0);
  const [newNote, setNewNote] = useState('');
  const [newExpense, setNewExpense] = useState({
    item: '', description: '', vendor: '', amount: 0, category: 'materials'
  });
  const [newTimeEntry, setNewTimeEntry] = useState({
    date: new Date().toISOString().split('T')[0],
    start_time: '09:00',
    end_time: '17:00',
    notes: ''
  });
  
  // Handyman Assignment State
  const [showAssignHandyman, setShowAssignHandyman] = useState(false);
  const [handymenList, setHandymenList] = useState([]);
  const [selectedHandymanId, setSelectedHandymanId] = useState('');
  const [assigningHandyman, setAssigningHandyman] = useState(false);
  
  // Job Quote & Pricing State
  const [editableItems, setEditableItems] = useState([]);
  const [discount, setDiscount] = useState({ type: 'amount', value: 0, percent: 0 });
  const [tax, setTax] = useState({ percent: 0, value: 0 });
  const [isEditingPricing, setIsEditingPricing] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [savingPricing, setSavingPricing] = useState(false);
  const [showPricingCalculator, setShowPricingCalculator] = useState(false);
  const [pricingCalcIndex, setPricingCalcIndex] = useState(null);

  useEffect(() => {
    if (jobId && open) {
      fetchJobDetails();
    }
  }, [jobId, open]);

  // Initialize editable items from quote data
  useEffect(() => {
    if (jobData?.quote?.items) {
      setEditableItems(jobData.quote.items.map(item => ({
        ...item,
        name: item.name || item.service || '',
        description: item.description || '',
        quantity: item.quantity || item.hours || 1,
        unit_cost: item.unit_cost || 0,
        markup_percent: item.markup_percent || 30,
        unit_price: item.unit_price || item.rate || 0,
        total: item.total || item.amount || 0,
        is_optional: item.is_optional || false
      })));
      
      setDiscount({
        type: jobData.quote.discount_type || 'amount',
        value: jobData.quote.discount || 0,
        percent: jobData.quote.discount_percent || 0
      });
      
      setTax({
        percent: jobData.quote.tax_percent || 0,
        value: jobData.quote.tax || 0
      });
    }
  }, [jobData?.quote]);

  const fetchJobDetails = async () => {
    setLoading(true);
    try {
      const [detailsRes, pnlRes] = await Promise.all([
        axiosInstance.get(`/jobs/${jobId}/full-details`),
        axiosInstance.get(`/jobs/${jobId}/pnl`)
      ]);
      
      setJobData({
        ...detailsRes.data,
        pnl: pnlRes.data.summary,
        handyman: pnlRes.data.handyman,
        timesheets: pnlRes.data.timesheets || detailsRes.data.timesheets || [],
        timeline: pnlRes.data.timeline || detailsRes.data.timeline || []
      });
    } catch (error) {
      toast.error('Failed to load job details');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchHandymen = async () => {
    try {
      const response = await axiosInstance.get('/handymen');
      setHandymenList(response.data || []);
    } catch (error) {
      console.error('Failed to fetch handymen:', error);
    }
  };

  const handleAssignHandyman = async () => {
    if (!selectedHandymanId) {
      toast.error('Please select a handyman');
      return;
    }
    
    setAssigningHandyman(true);
    try {
      await axiosInstance.patch(`/jobs/${jobId}`, {
        handyman_id: selectedHandymanId
      });
      toast.success('Handyman assigned successfully');
      setShowAssignHandyman(false);
      setSelectedHandymanId('');
      fetchJobDetails();
    } catch (error) {
      toast.error('Failed to assign handyman');
    } finally {
      setAssigningHandyman(false);
    }
  };

  const openAssignHandymanDialog = () => {
    fetchHandymen();
    setSelectedHandymanId(jobData?.job?.handyman_id || '');
    setShowAssignHandyman(true);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', { timeZone: 'America/Phoenix', 
      timeZone: 'America/Phoenix', month: 'short', day: 'numeric', year: 'numeric' 
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString('en-US', { timeZone: 'America/Phoenix', 
      timeZone: 'America/Phoenix', month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit', hour12: true
    });
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleTimeString('en-US', { timeZone: 'America/Phoenix', 
      timeZone: 'America/Phoenix', hour: 'numeric', minute: '2-digit', hour12: true 
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  const toggleExpand = (type, index) => {
    const setters = {
      lineItem: setExpandedLineItems,
      expense: setExpandedExpenses,
      timeEntry: setExpandedTimeEntries,
      visit: setExpandedVisits,
      invoice: setExpandedInvoices
    };
    setters[type]?.(prev => ({ ...prev, [index]: !prev[index] }));
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-700',
      scheduled: 'bg-blue-100 text-blue-700',
      assigned: 'bg-purple-100 text-purple-700',
      in_progress: 'bg-orange-100 text-orange-700',
      completed: 'bg-green-100 text-green-700',
      cancelled: 'bg-red-100 text-red-700',
      paid: 'bg-green-100 text-green-700',
      sent: 'bg-blue-100 text-blue-700',
      overdue: 'bg-red-100 text-red-700',
      draft: 'bg-gray-100 text-gray-700'
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  // Expense handlers
  const handleAddExpense = async () => {
    if (!newExpense.item || !newExpense.amount) {
      toast.error('Please fill in item name and amount');
      return;
    }
    try {
      await axiosInstance.post(`/jobs/${jobId}/expenses`, {
        ...newExpense,
        expense_date: new Date().toISOString()
      });
      toast.success('Expense added');
      setNewExpense({ item: '', description: '', vendor: '', amount: 0, category: 'materials' });
      setShowAddExpense(false);
      fetchJobDetails();
    } catch (error) {
      toast.error('Failed to add expense');
    }
  };

  // Time entry handlers
  const handleAddTimeEntry = async () => {
    try {
      const startDateTime = new Date(`${newTimeEntry.date}T${newTimeEntry.start_time}`);
      const endDateTime = new Date(`${newTimeEntry.date}T${newTimeEntry.end_time}`);
      const hours = (endDateTime - startDateTime) / (1000 * 60 * 60);
      
      await axiosInstance.post(`/timesheets`, {
        job_id: jobId,
        handyman_id: jobData?.job?.handyman_id,
        clock_in: startDateTime.toISOString(),
        clock_out: endDateTime.toISOString(),
        total_hours: hours,
        notes: newTimeEntry.notes
      });
      toast.success('Time entry added');
      setNewTimeEntry({ date: new Date().toISOString().split('T')[0], start_time: '09:00', end_time: '17:00', notes: '' });
      setShowAddTimeEntry(false);
      fetchJobDetails();
    } catch (error) {
      toast.error('Failed to add time entry');
    }
  };

  // Note handlers
  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    try {
      await axiosInstance.post(`/jobs/${jobId}/internal-messages`, {
        message: newNote,
        author: JSON.parse(localStorage.getItem('user') || '{}').name || 'Admin'
      });
      toast.success('Note added');
      setNewNote('');
      setShowAddNote(false);
      fetchJobDetails();
    } catch (error) {
      toast.error('Failed to add note');
    }
  };

  // Invoice handlers - Open InvoiceView for creating new invoice
  // Fetch actual payments for this job/customer
  const fetchActualPayments = async () => {
    try {
      const customerId = jobData.job?.customer_id;
      if (!customerId) return;
      
      const res = await axiosInstance.get(`/payments?customer_id=${customerId}&job_id=${jobId}`);
      const payments = res.data || [];
      const totalPaid = payments
        .filter(p => p.status === 'completed')
        .reduce((sum, p) => sum + (p.amount || 0), 0);
      setActualPayments(totalPaid);
      setAdvanceAmount(totalPaid); // Default to actual payments
    } catch (error) {
      console.log('Could not fetch payments:', error);
      setActualPayments(0);
      setAdvanceAmount(0);
    }
  };

  // Create Invoice directly and open professional template
  const openCreateInvoiceDialog = async () => {
    setCreatingInvoice(true);
    try {
      // Create invoice from job with 0 advance (user can edit in the invoice template)
      const res = await axiosInstance.post(`/jobs/${jobId}/generate-invoice?advance_paid=0`);
      const invoiceId = res.data.invoice_id;
      
      // Store the invoice ID and open dialog - let InvoiceView fetch the data
      setSelectedInvoice({ id: invoiceId, invoice_number: res.data.invoice_number });
      setShowInvoiceView(true);
      
      toast.success(`Invoice ${res.data.invoice_number} created`);
      fetchJobDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create invoice');
    } finally {
      setCreatingInvoice(false);
    }
  };

  const handleCreateInvoice = async () => {
    setCreatingInvoice(true);
    try {
      // Create invoice from job with advance amount
      const res = await axiosInstance.post(`/jobs/${jobId}/generate-invoice?advance_paid=${advanceAmount}`);
      
      // Store the invoice ID and open dialog - let InvoiceView fetch the data
      setSelectedInvoice({ id: res.data.invoice_id, invoice_number: res.data.invoice_number });
      setShowCreateInvoiceDialog(false);
      setShowInvoiceView(true);
      
      toast.success(`Invoice ${res.data.invoice_number} created`);
      fetchJobDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create invoice');
    } finally {
      setCreatingInvoice(false);
    }
  };

  // View existing invoice in professional template
  const handleViewInvoice = (invoice) => {
    setSelectedInvoice(invoice);
    setShowInvoiceView(true);
  };

  const handleSendInvoice = async (invoiceId) => {
    try {
      await axiosInstance.post(`/invoices/${invoiceId}/send`);
      toast.success('Invoice sent to customer');
      fetchJobDetails();
    } catch (error) {
      toast.error('Failed to send invoice');
    }
  };

  // Quote/Pricing handlers
  const updateEditableItem = (index, field, value) => {
    const newItems = [...editableItems];
    newItems[index][field] = value;
    
    if (field === 'unit_cost' || field === 'markup_percent') {
      const cost = field === 'unit_cost' ? value : newItems[index].unit_cost;
      const markup = field === 'markup_percent' ? value : newItems[index].markup_percent;
      newItems[index].unit_price = cost * (1 + markup / 100);
    }
    
    if (['quantity', 'unit_price', 'unit_cost', 'markup_percent'].includes(field)) {
      newItems[index].total = newItems[index].quantity * newItems[index].unit_price;
    }
    
    setEditableItems(newItems);
    setHasUnsavedChanges(true);
  };

  const addEditableItem = () => {
    setEditableItems([...editableItems, {
      name: '', description: '', quantity: 1, unit_cost: 0,
      markup_percent: 30, unit_price: 0, total: 0, is_optional: false
    }]);
    setHasUnsavedChanges(true);
  };

  const removeEditableItem = (index) => {
    if (editableItems.length > 1) {
      setEditableItems(editableItems.filter((_, i) => i !== index));
      setHasUnsavedChanges(true);
    }
  };

  // Calculate totals
  const editableSubtotal = editableItems.reduce((sum, item) => sum + (item.is_optional ? 0 : (item.total || 0)), 0);
  const editableDiscountAmount = discount.type === 'percent' 
    ? (editableSubtotal * (discount.percent || 0) / 100) 
    : (discount.value || 0);
  const editableTaxableAmount = editableSubtotal - editableDiscountAmount;
  const editableTaxAmount = editableTaxableAmount * ((tax.percent || 0) / 100);
  const editableGrandTotal = editableTaxableAmount + editableTaxAmount;

  const handleSavePricing = async () => {
    if (editableItems.some(item => !item.name)) {
      toast.error('Please fill in all line item names');
      return;
    }

    setSavingPricing(true);
    try {
      if (jobData?.quote?.id) {
        await axiosInstance.patch(`/quotes/${jobData.quote.id}`, {
          items: editableItems,
          subtotal: editableSubtotal,
          tax: editableTaxAmount,
          tax_percent: tax.percent,
          discount: editableDiscountAmount,
          discount_type: discount.type,
          discount_percent: discount.type === 'percent' ? discount.percent : 0,
          total_amount: editableGrandTotal
        });
        toast.success('Job pricing updated successfully');
        setHasUnsavedChanges(false);
        setIsEditingPricing(false);
        fetchJobDetails();
      } else {
        toast.error('No quote associated with this job');
      }
    } catch (error) {
      toast.error('Failed to update pricing');
    } finally {
      setSavingPricing(false);
    }
  };

  const handleCancelPricingEdit = () => {
    if (jobData?.quote?.items) {
      setEditableItems(jobData.quote.items.map(item => ({
        ...item, name: item.name || item.service || '', description: item.description || '',
        quantity: item.quantity || item.hours || 1, unit_cost: item.unit_cost || 0,
        markup_percent: item.markup_percent || 30, unit_price: item.unit_price || item.rate || 0,
        total: item.total || item.amount || 0, is_optional: item.is_optional || false
      })));
      setDiscount({ type: jobData.quote.discount_type || 'amount', value: jobData.quote.discount || 0, percent: jobData.quote.discount_percent || 0 });
      setTax({ percent: jobData.quote.tax_percent || 0, value: jobData.quote.tax || 0 });
    }
    setHasUnsavedChanges(false);
    setIsEditingPricing(false);
  };

  // Calculate dashboard stats
  const calculateDashboardStats = () => {
    if (!jobData) return null;
    
    const invoices = jobData.invoices || [];
    const expenses = jobData.expenses || [];
    const timesheets = jobData.timesheets || [];
    
    const totalInvoiced = invoices.reduce((sum, inv) => sum + (inv.total_amount || 0), 0);
    const totalPaid = invoices.filter(inv => inv.status === 'paid').reduce((sum, inv) => sum + (inv.total_amount || 0), 0);
    const totalPending = invoices.filter(inv => inv.status === 'sent' || inv.status === 'pending').reduce((sum, inv) => sum + (inv.total_amount || 0), 0);
    const totalOverdue = invoices.filter(inv => inv.status === 'overdue').reduce((sum, inv) => sum + (inv.total_amount || 0), 0);
    const advanceReceived = invoices.filter(inv => inv.invoice_type === 'advance' && inv.status === 'paid').reduce((sum, inv) => sum + (inv.total_amount || 0), 0);
    
    const totalExpenses = expenses.reduce((sum, exp) => sum + (exp.amount || 0), 0);
    const totalHours = timesheets.reduce((sum, ts) => sum + (ts.total_hours || 0), 0);
    const laborCost = jobData.pnl?.labor_cost || 0;
    
    return {
      jobTotal: jobData.job?.quote_amount || jobData.pnl?.quote_amount || 0,
      totalInvoiced,
      totalPaid,
      totalPending,
      totalOverdue,
      advanceReceived,
      receivable: totalInvoiced - totalPaid,
      totalExpenses,
      totalHours,
      laborCost,
      profit: jobData.pnl?.profit || 0,
      margin: jobData.pnl?.margin_percent || 0,
      invoiceCount: invoices.length,
      paidInvoices: invoices.filter(inv => inv.status === 'paid').length,
      pendingInvoices: invoices.filter(inv => inv.status !== 'paid' && inv.status !== 'cancelled').length
    };
  };

  const stats = calculateDashboardStats();

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[95vh] overflow-hidden p-0">
        <div className="overflow-y-auto max-h-[95vh]">
          {loading ? (
            <div className="flex items-center justify-center h-96">
              <Loader2 className="w-8 h-8 animate-spin text-[#595959]" />
            </div>
          ) : jobData ? (
            <div className="p-6 space-y-6">
              {/* Header */}
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    Job #{jobData.job?.id?.slice(0, 8).toUpperCase()}
                  </h2>
                  <div className="flex items-center gap-3 mt-2">
                    <Badge className={getStatusColor(jobData.job?.status)}>
                      {jobData.job?.status?.replace('_', ' ')}
                    </Badge>
                    {jobData.quote?.quote_number && (
                      <span className="text-sm text-gray-500">From Quote: #{jobData.quote.quote_number}</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Eye className="w-4 h-4" />
                    <span>Hide Profitability</span>
                    <Switch checked={hideProfitability} onCheckedChange={setHideProfitability} />
                  </div>
                </div>
              </div>

              {/* Job Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {/* Job Status Card */}
                <Card>
                  <CardContent className="p-4">
                    <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                      <Activity className="w-4 h-4 text-[#595959]" />
                      Job Status
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-500">Status</span>
                        <Badge className={getStatusColor(jobData.job?.status)}>{jobData.job?.status?.replace('_', ' ')}</Badge>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Scheduled</span>
                        <span>{formatDate(jobData.job?.scheduled_date)}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-500">Assigned To</span>
                        <div className="flex items-center gap-2">
                          <span>{jobData.handyman?.name || jobData.job?.handyman_name || 'Unassigned'}</span>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-6 w-6 p-0" 
                            onClick={openAssignHandymanDialog}
                            data-testid="assign-handyman-btn"
                          >
                            <Edit className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Service</span>
                        <span>{jobData.job?.service_name || 'General'}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Labor Summary */}
                <Card>
                  <CardContent className="p-4">
                    <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                      <Timer className="w-4 h-4 text-[#595959]" />
                      Labor Summary
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-500">Total Hours</span>
                        <span className="font-medium">{stats?.totalHours?.toFixed(2) || 0}h</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Hourly Rate</span>
                        <span>{formatCurrency(jobData.pnl?.hourly_rate || 25)}/h</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Labor Cost</span>
                        <span className="font-medium">{formatCurrency(stats?.laborCost)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Time Entries</span>
                        <span>{jobData.timesheets?.length || 0}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Expenses Summary */}
                <Card>
                  <CardContent className="p-4">
                    <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                      <Receipt className="w-4 h-4 text-[#595959]" />
                      Expenses Summary
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-500">Total Expenses</span>
                        <span className="font-medium">{formatCurrency(stats?.totalExpenses)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Materials</span>
                        <span>{formatCurrency(jobData.expenses?.filter(e => e.category === 'materials').reduce((s, e) => s + e.amount, 0) || 0)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Travel</span>
                        <span>{formatCurrency(jobData.expenses?.filter(e => e.category === 'travel').reduce((s, e) => s + e.amount, 0) || 0)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Expense Count</span>
                        <span>{jobData.expenses?.length || 0}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Package Info Card */}
              {jobData.job?.use_package && jobData.customer_package && (
                <Card className="border-blue-200 bg-blue-50/40">
                  <CardContent className="p-4">
                    <h4 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
                      <Clock className="w-4 h-4 text-blue-600" />
                      Package: {jobData.customer_package.package_name}
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                      <div className="text-center p-2 bg-white rounded border">
                        <div className="text-lg font-bold text-gray-900">{jobData.customer_package.total_hours}h</div>
                        <div className="text-xs text-gray-500">Total</div>
                      </div>
                      <div className="text-center p-2 bg-white rounded border">
                        <div className="text-lg font-bold text-gray-900">{jobData.customer_package.used_hours}h</div>
                        <div className="text-xs text-gray-500">Used</div>
                      </div>
                      <div className="text-center p-2 bg-white rounded border">
                        <div className="text-lg font-bold text-blue-700">{jobData.customer_package.remaining_hours}h</div>
                        <div className="text-xs text-gray-500">Remaining</div>
                      </div>
                      <div className="text-center p-2 bg-white rounded border">
                        <div className="text-lg font-bold text-gray-900">
                          {(jobData.quote?.items || []).filter(i => i.deduct_from_package).length} / {(jobData.quote?.items || []).length}
                        </div>
                        <div className="text-xs text-gray-500">Package Items</div>
                      </div>
                    </div>
                    {jobData.job?.package_deduction_summary && (
                      <div className="mt-3 p-2 bg-green-50 border border-green-200 rounded text-sm">
                        <span className="font-medium text-green-800">Deduction Complete:</span>{' '}
                        {jobData.job.package_deduction_summary.hours_deducted}h deducted
                        {jobData.job.package_deduction_summary.overage_hours > 0 && (
                          <span className="text-orange-700 ml-2">
                            | Overage: {jobData.job.package_deduction_summary.overage_hours}h (${jobData.job.package_deduction_summary.overage_charge})
                          </span>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Customer & Job Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <User className="w-5 h-5 text-[#595959]" />
                      Customer
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-gray-400" />
                      <span className="font-medium">{jobData.job?.customer_name || 'N/A'}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-gray-400" />
                      <span>{jobData.job?.address || 'No address'}</span>
                    </div>
                    {jobData.customer?.email && (
                      <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-gray-400" />
                        <span>{jobData.customer.email}</span>
                      </div>
                    )}
                    {jobData.customer?.phone && (
                      <div className="flex items-center gap-2">
                        <Phone className="w-4 h-4 text-gray-400" />
                        <span>{jobData.customer.phone}</span>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Profitability Card */}
                {!hideProfitability && (
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <BarChart3 className="w-5 h-5 text-[#595959]" />
                        Profitability
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-center mb-4 p-3 bg-gradient-to-r from-teal-50 to-green-50 rounded-lg">
                        <p className="text-sm text-gray-600 mb-1">Profit Margin</p>
                        <p className={`text-3xl font-bold ${jobData.pnl?.margin_percent >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {jobData.pnl?.margin_percent || 0}%
                        </p>
                      </div>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between py-1 border-b">
                          <span className="text-gray-600">Job Selling Price</span>
                          <span className="font-semibold">{formatCurrency(jobData.pnl?.quote_amount)}</span>
                        </div>
                        <div className="flex justify-between py-1 border-b">
                          <span className="text-gray-600">Line Item Cost</span>
                          <span>{formatCurrency(jobData.pnl?.line_item_cost || 0)}</span>
                        </div>
                        <div className="flex justify-between py-1 border-b">
                          <span className="text-gray-600">Labor Cost</span>
                          <span>{formatCurrency(jobData.pnl?.labor_cost)}</span>
                        </div>
                        <div className="flex justify-between py-1 border-b">
                          <span className="text-gray-600">Expenses</span>
                          <span>{formatCurrency(jobData.pnl?.total_expenses)}</span>
                        </div>
                        <div className="flex justify-between py-1 border-b bg-gray-50 px-2 rounded">
                          <span className="font-medium">Total Cost</span>
                          <span className="font-medium">{formatCurrency(jobData.pnl?.total_cost)}</span>
                        </div>
                        <div className="flex justify-between pt-2">
                          <span className="font-semibold">Profit</span>
                          <span className={`font-bold text-lg ${jobData.pnl?.profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {formatCurrency(jobData.pnl?.profit)}
                          </span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>

              {/* Job Quote & Pricing Section */}
              {(jobData.quote?.items?.length > 0 || isEditingPricing) && (
                <Card>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <ShoppingCart className="w-5 h-5 text-[#595959]" />
                        Job Quote & Pricing
                      </CardTitle>
                      <div className="flex items-center gap-2">
                        {isEditingPricing ? (
                          <>
                            <Button size="sm" variant="outline" onClick={handleCancelPricingEdit} disabled={savingPricing}>
                              <X className="w-4 h-4 mr-1" /> Cancel
                            </Button>
                            <Button size="sm" onClick={handleSavePricing} disabled={savingPricing} className="bg-[#595959] hover:bg-[#404040]">
                              <Save className="w-4 h-4 mr-1" /> {savingPricing ? 'Saving...' : 'Save Changes'}
                            </Button>
                          </>
                        ) : (
                          <Button size="sm" variant="outline" onClick={() => setIsEditingPricing(true)}>
                            <Edit className="w-4 h-4 mr-1" /> Edit Pricing
                          </Button>
                        )}
                      </div>
                    </div>
                    {hasUnsavedChanges && <p className="text-sm text-orange-600 mt-1">You have unsaved changes</p>}
                  </CardHeader>
                  <CardContent>
                    {isEditingPricing ? (
                      <div className="space-y-4">
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead>
                              <tr className="border-b bg-gray-50">
                                <th className="text-left p-3 font-medium text-gray-600">Product / Service</th>
                                <th className="text-left p-3 font-medium text-gray-600">Description</th>
                                <th className="text-center p-3 font-medium text-gray-600 w-20">Qty</th>
                                {!hideProfitability && <th className="text-right p-3 font-medium text-gray-600 w-28">Cost</th>}
                                <th className="text-right p-3 font-medium text-gray-600 w-32">Unit Price</th>
                                <th className="text-right p-3 font-medium text-gray-600 w-28">Total</th>
                                <th className="p-3 w-12"></th>
                              </tr>
                            </thead>
                            <tbody>
                              {editableItems.map((item, index) => (
                                <tr key={index} className="border-b">
                                  <td className="p-2">
                                    <Input value={item.name} onChange={(e) => updateEditableItem(index, 'name', e.target.value)} placeholder="Item name *" className={!item.name ? 'border-red-300' : ''} />
                                    <div className="flex items-center gap-2 mt-1">
                                      <Checkbox id={`opt-${index}`} checked={item.is_optional} onCheckedChange={(checked) => updateEditableItem(index, 'is_optional', checked)} />
                                      <Label htmlFor={`opt-${index}`} className="text-xs text-gray-500 cursor-pointer">Optional</Label>
                                    </div>
                                  </td>
                                  <td className="p-2"><Input value={item.description} onChange={(e) => updateEditableItem(index, 'description', e.target.value)} placeholder="Description" /></td>
                                  <td className="p-2"><Input type="number" min="1" value={item.quantity} onChange={(e) => updateEditableItem(index, 'quantity', parseFloat(e.target.value) || 1)} className="text-center" /></td>
                                  {!hideProfitability && (
                                    <td className="p-2">
                                      <div className="relative">
                                        <Input type="number" value={item.unit_cost} onChange={(e) => updateEditableItem(index, 'unit_cost', parseFloat(e.target.value) || 0)} className="text-right pr-8" />
                                        <Button type="button" variant="ghost" size="sm" className="absolute right-0 top-0 h-full px-2 text-[#595959]" onClick={() => { setPricingCalcIndex(index); setShowPricingCalculator(true); }} title="Markup calculator">
                                          <Calculator className="w-4 h-4" />
                                        </Button>
                                      </div>
                                    </td>
                                  )}
                                  <td className="p-2"><Input type="number" value={item.unit_price.toFixed(2)} onChange={(e) => updateEditableItem(index, 'unit_price', parseFloat(e.target.value) || 0)} className="text-right" /></td>
                                  <td className="p-2"><Input type="number" value={item.total.toFixed(2)} disabled className="text-right bg-gray-100 font-medium" /></td>
                                  <td className="p-2 text-center">
                                    <Button type="button" variant="ghost" size="sm" onClick={() => removeEditableItem(index)} disabled={editableItems.length === 1} className="text-red-500 hover:text-red-700">
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        <Button variant="outline" onClick={addEditableItem} className="w-full"><Plus className="w-4 h-4 mr-2" /> Add Line Item</Button>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t">
                          <div className="space-y-3">
                            <Label className="font-medium">Discount</Label>
                            <div className="flex gap-2">
                              <select value={discount.type} onChange={(e) => setDiscount({ ...discount, type: e.target.value })} className="px-3 py-2 border rounded-md text-sm">
                                <option value="amount">$ Amount</option>
                                <option value="percent">% Percent</option>
                              </select>
                              <Input type="number" min="0" value={discount.type === 'percent' ? discount.percent : discount.value} onChange={(e) => { const val = parseFloat(e.target.value) || 0; if (discount.type === 'percent') { setDiscount({ ...discount, percent: val }); } else { setDiscount({ ...discount, value: val }); } setHasUnsavedChanges(true); }} className="flex-1" />
                            </div>
                            <Label className="font-medium">Tax (%)</Label>
                            <Input type="number" min="0" max="100" value={tax.percent} onChange={(e) => { setTax({ ...tax, percent: parseFloat(e.target.value) || 0 }); setHasUnsavedChanges(true); }} className="w-32" />
                          </div>
                          <div className="p-4 bg-gray-50 rounded-lg space-y-2">
                            <div className="flex justify-between text-sm"><span className="text-gray-600">Subtotal</span><span>{formatCurrency(editableSubtotal)}</span></div>
                            <div className="flex justify-between text-sm"><span className="text-gray-600">Discount {discount.type === 'percent' && `(${discount.percent}%)`}</span><span className="text-red-600">-{formatCurrency(editableDiscountAmount)}</span></div>
                            <div className="flex justify-between text-sm"><span className="text-gray-600">Tax ({tax.percent}%)</span><span>{formatCurrency(editableTaxAmount)}</span></div>
                            <hr />
                            <div className="flex justify-between font-bold text-[#595959]"><span>Grand Total</span><span>{formatCurrency(editableGrandTotal)}</span></div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="border-b bg-gray-50">
                              <th className="text-left p-3 font-medium text-gray-600 w-8"></th>
                              <th className="text-left p-3 font-medium text-gray-600">Product / Service</th>
                              <th className="text-center p-3 font-medium text-gray-600">Qty</th>
                              {!hideProfitability && <th className="text-right p-3 font-medium text-gray-600">Cost</th>}
                              <th className="text-right p-3 font-medium text-gray-600">Price</th>
                              <th className="text-right p-3 font-medium text-gray-600">Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {jobData.quote.items.map((item, index) => (
                              <React.Fragment key={`line-item-${index}`}>
                                <tr className="border-b hover:bg-gray-50 cursor-pointer" onClick={() => toggleExpand('lineItem', index)}>
                                  <td className="p-3">{expandedLineItems[index] ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}</td>
                                  <td className="p-3"><span className="font-medium">{item.name || item.service}</span>{item.is_optional && <Badge className="ml-2 bg-orange-100 text-orange-700">Optional</Badge>}{item.deduct_from_package && <Badge className="ml-2 bg-blue-100 text-blue-800">PKG</Badge>}</td>
                                  <td className="p-3 text-center">{item.quantity || item.hours || 1}</td>
                                  {!hideProfitability && <td className="p-3 text-right text-gray-600">{formatCurrency(item.unit_cost || 0)}</td>}
                                  <td className="p-3 text-right">{formatCurrency(item.unit_price || item.rate || 0)}</td>
                                  <td className="p-3 text-right font-medium">{formatCurrency(item.total || item.amount || 0)}</td>
                                </tr>
                                {expandedLineItems[index] && (
                                  <tr className="bg-gray-50">
                                    <td colSpan={hideProfitability ? 5 : 6} className="p-4">
                                      <div className="ml-8 space-y-3">
                                        {item.description && <div><p className="text-sm font-medium text-gray-700">Description</p><p className="text-sm text-gray-600">{item.description}</p></div>}
                                        {!hideProfitability && (
                                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-3 bg-white rounded border">
                                            <div><p className="text-xs text-gray-500">Unit Cost</p><p className="font-medium">{formatCurrency(item.unit_cost || 0)}</p></div>
                                            <div><p className="text-xs text-gray-500">Markup %</p><p className="font-medium">{item.markup_percent || 0}%</p></div>
                                            <div><p className="text-xs text-gray-500">Selling Price</p><p className="font-medium">{formatCurrency(item.unit_price || item.rate || 0)}</p></div>
                                            <div><p className="text-xs text-gray-500">Item Profit</p><p className="font-medium text-green-600">{formatCurrency(((item.unit_price || 0) - (item.unit_cost || 0)) * (item.quantity || 1))}</p></div>
                                          </div>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            ))}
                          </tbody>
                          <tfoot>
                            <tr className="border-t"><td colSpan={hideProfitability ? 4 : 5} className="p-3 text-right text-gray-600">Subtotal</td><td className="p-3 text-right font-medium">{formatCurrency(jobData.quote.subtotal || editableSubtotal)}</td></tr>
                            {(jobData.quote.discount > 0 || jobData.quote.discount_percent > 0) && <tr><td colSpan={hideProfitability ? 4 : 5} className="p-3 text-right text-gray-600">Discount</td><td className="p-3 text-right font-medium text-red-600">-{formatCurrency(jobData.quote.discount || 0)}</td></tr>}
                            {(jobData.quote.tax > 0 || jobData.quote.tax_percent > 0) && <tr><td colSpan={hideProfitability ? 4 : 5} className="p-3 text-right text-gray-600">Tax ({jobData.quote.tax_percent || 0}%)</td><td className="p-3 text-right font-medium">{formatCurrency(jobData.quote.tax || 0)}</td></tr>}
                            <tr className="bg-gray-50 font-medium"><td colSpan={hideProfitability ? 4 : 5} className="p-3 text-right">Grand Total</td><td className="p-3 text-right font-bold text-[#595959]">{formatCurrency(jobData.quote.total_amount)}</td></tr>
                          </tfoot>
                        </table>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Labor / Time Tracking Section */}
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Timer className="w-5 h-5 text-[#595959]" />
                      Labor / Time Tracking
                    </CardTitle>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">{jobData.pnl?.total_hours || 0} hours logged</Badge>
                      <Button size="sm" variant="outline" onClick={() => setShowAddTimeEntry(true)}>
                        <Plus className="w-4 h-4 mr-1" /> Add Time
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {jobData.timesheets?.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b bg-gray-50">
                            <th className="text-left p-3 font-medium text-gray-600 w-8"></th>
                            <th className="text-left p-3 font-medium text-gray-600">Technician</th>
                            <th className="text-left p-3 font-medium text-gray-600">Date</th>
                            <th className="text-center p-3 font-medium text-gray-600">Start</th>
                            <th className="text-center p-3 font-medium text-gray-600">End</th>
                            <th className="text-right p-3 font-medium text-gray-600">Hours</th>
                            {!hideProfitability && <th className="text-right p-3 font-medium text-gray-600">Cost</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {jobData.timesheets.map((ts, index) => (
                            <React.Fragment key={`ts-${index}`}>
                              <tr className="border-b hover:bg-gray-50 cursor-pointer" onClick={() => toggleExpand('timeEntry', index)}>
                                <td className="p-3">{expandedTimeEntries[index] ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}</td>
                                <td className="p-3 font-medium">{ts.handyman_name || jobData.handyman?.name || 'Handyman'}</td>
                                <td className="p-3">{formatDate(ts.date || ts.clock_in)}</td>
                                <td className="p-3 text-center">{formatTime(ts.clock_in)}</td>
                                <td className="p-3 text-center">{ts.clock_out ? formatTime(ts.clock_out) : '—'}</td>
                                <td className="p-3 text-right font-medium">{(ts.total_hours || 0).toFixed(2)}h</td>
                                {!hideProfitability && <td className="p-3 text-right">{formatCurrency((ts.total_hours || 0) * (jobData.pnl?.hourly_rate || 25))}</td>}
                              </tr>
                              {expandedTimeEntries[index] && (
                                <tr className="bg-gray-50">
                                  <td colSpan={hideProfitability ? 6 : 7} className="p-4">
                                    <div className="ml-8 grid grid-cols-2 md:grid-cols-4 gap-4">
                                      {ts.location && <div><p className="text-xs text-gray-500 flex items-center gap-1"><Map className="w-3 h-3" /> Location</p><p className="text-sm">{ts.location}</p></div>}
                                      {ts.notes && <div className="col-span-2"><p className="text-xs text-gray-500 flex items-center gap-1"><MessageSquare className="w-3 h-3" /> Notes</p><p className="text-sm">{ts.notes}</p></div>}
                                      {ts.breaks && ts.breaks.length > 0 && <div><p className="text-xs text-gray-500">Breaks</p><p className="text-sm">{ts.breaks.length} break(s)</p></div>}
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr className="bg-gray-50 font-medium">
                            <td colSpan={5} className="p-3 text-right">Total Labor</td>
                            <td className="p-3 text-right">{(jobData.pnl?.total_hours || 0).toFixed(2)}h</td>
                            {!hideProfitability && <td className="p-3 text-right font-bold text-[#595959]">{formatCurrency(jobData.pnl?.labor_cost)}</td>}
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <Timer className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p>No time entries yet. Time tracked by your team will show here.</p>
                      <Button size="sm" variant="outline" className="mt-3" onClick={() => setShowAddTimeEntry(true)}>
                        <Plus className="w-4 h-4 mr-1" /> Add Time Entry
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Expenses Section */}
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Receipt className="w-5 h-5 text-[#595959]" />
                      Expenses
                    </CardTitle>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">{formatCurrency(jobData.pnl?.total_expenses || 0)} total</Badge>
                      <Button size="sm" variant="outline" onClick={() => setShowAddExpense(true)}>
                        <Plus className="w-4 h-4 mr-1" /> Add Expense
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {jobData.expenses?.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b bg-gray-50">
                            <th className="text-left p-3 font-medium text-gray-600 w-8"></th>
                            <th className="text-left p-3 font-medium text-gray-600">Item</th>
                            <th className="text-left p-3 font-medium text-gray-600">Category</th>
                            <th className="text-left p-3 font-medium text-gray-600">Vendor</th>
                            <th className="text-left p-3 font-medium text-gray-600">Date</th>
                            <th className="text-right p-3 font-medium text-gray-600">Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {jobData.expenses.map((exp, index) => {
                            // Get attachment URL - handyman uses bill_url, admin uses receipt_url
                            const attachmentUrl = exp.bill_url || exp.receipt_url;
                            const isImageAttachment = attachmentUrl && /\.(jpg|jpeg|png|gif|webp)$/i.test(attachmentUrl);
                            
                            return (
                            <React.Fragment key={`exp-${index}`}>
                              <tr className="border-b hover:bg-gray-50 cursor-pointer" onClick={() => toggleExpand('expense', index)}>
                                <td className="p-3">{expandedExpenses[index] ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}</td>
                                <td className="p-3 font-medium">{exp.item || exp.expense_details || 'Expense'}</td>
                                <td className="p-3"><Badge variant="outline" className="capitalize">{exp.category || 'General'}</Badge></td>
                                <td className="p-3 text-gray-600">{exp.vendor || exp.handyman_name || '—'}</td>
                                <td className="p-3">{formatDate(exp.expense_date || exp.created_at)}</td>
                                <td className="p-3 text-right font-medium">{formatCurrency(exp.amount)}</td>
                              </tr>
                              {expandedExpenses[index] && (
                                <tr className="bg-gray-50">
                                  <td colSpan={6} className="p-4">
                                    <div className="ml-8 space-y-3">
                                      {(exp.description || exp.expense_details) && (
                                        <div>
                                          <p className="text-xs text-gray-500">Description</p>
                                          <p className="text-sm">{exp.description || exp.expense_details}</p>
                                        </div>
                                      )}
                                      {attachmentUrl && (
                                        <div>
                                          <p className="text-xs text-gray-500 mb-2">Receipt / Attachment</p>
                                          {isImageAttachment ? (
                                            <a href={attachmentUrl} target="_blank" rel="noopener noreferrer" className="block">
                                              <img 
                                                src={attachmentUrl} 
                                                alt="Receipt" 
                                                className="max-w-xs max-h-48 object-contain rounded-lg border shadow-sm hover:shadow-md transition-shadow"
                                              />
                                            </a>
                                          ) : (
                                            <a href={attachmentUrl} target="_blank" rel="noopener noreferrer" className="text-sm text-[#595959] flex items-center gap-1 hover:underline">
                                              <Paperclip className="w-3 h-3" /> View Attachment
                                            </a>
                                          )}
                                        </div>
                                      )}
                                      {exp.handyman_name && (
                                        <div>
                                          <p className="text-xs text-gray-500">Added By</p>
                                          <p className="text-sm">{exp.handyman_name}</p>
                                        </div>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                            );
                          })}
                        </tbody>
                        <tfoot>
                          <tr className="bg-gray-50 font-medium">
                            <td colSpan={5} className="p-3 text-right">Total Expenses</td>
                            <td className="p-3 text-right font-bold text-[#595959]">{formatCurrency(jobData.pnl?.total_expenses)}</td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <Receipt className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p>No expenses recorded yet.</p>
                      <Button size="sm" variant="outline" className="mt-3" onClick={() => setShowAddExpense(true)}>
                        <Plus className="w-4 h-4 mr-1" /> Add Expense
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Visit History / Timeline Section */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Activity className="w-5 h-5 text-[#595959]" />
                    Visit History / Timeline
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {jobData.timeline?.length > 0 ? (
                    <div className="relative">
                      <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200"></div>
                      <div className="space-y-4">
                        {jobData.timeline.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)).map((event, index) => (
                          <div key={index} className="relative pl-10">
                            <div className={`absolute left-2 w-4 h-4 rounded-full border-2 ${
                              event.event_type === 'completed_job' ? 'bg-green-500 border-green-500' :
                              event.event_type === 'started_job' ? 'bg-blue-500 border-blue-500' :
                              event.event_type === 'assigned' ? 'bg-purple-500 border-purple-500' :
                              'bg-gray-300 border-gray-300'
                            }`}></div>
                            <div className="bg-gray-50 rounded-lg p-3">
                              <div className="flex items-center justify-between mb-1">
                                <span className="font-medium capitalize">{event.event_type?.replace(/_/g, ' ') || 'Event'}</span>
                                <span className="text-xs text-gray-500">{formatDateTime(event.timestamp)}</span>
                              </div>
                              {event.notes && <p className="text-sm text-gray-600">{event.notes}</p>}
                              {event.performed_by && <p className="text-xs text-gray-400 mt-1">By: {event.performed_by}</p>}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <Activity className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p>No visit history recorded yet.</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Job Activity Timeline - Using shared component */}
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-2">
                    <Clock className="w-5 h-5 text-[#595959]" />
                    <CardTitle className="text-lg">Job Activity Timeline</CardTitle>
                    <Badge variant="outline" className="ml-auto">
                      {(jobData.timeline?.length || 0) + (jobData.job?.progress?.length || 0)} events
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Combine timeline events and progress updates */}
                  {(() => {
                    // Merge timeline and progress into one array
                    const allEvents = [
                      ...(jobData.timeline || []).map(t => ({
                        ...t,
                        event_type: t.event_type,
                        timestamp: t.timestamp,
                        notes: t.notes,
                        handyman_name: t.handyman_name || t.user_name,
                        photos: t.photos || [],
                        percentage_complete: t.percentage_complete
                      })),
                      ...(jobData.job?.progress || []).map(p => ({
                        ...p,
                        event_type: 'progress_update',
                        timestamp: p.timestamp,
                        notes: p.work_completed || p.notes,
                        handyman_name: p.user_name || p.handyman_name,
                        photos: p.photos || [],
                        percentage_complete: p.percentage_complete
                      }))
                    ];
                    
                    // Sort by timestamp (newest first)
                    allEvents.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
                    
                    if (allEvents.length === 0) {
                      return (
                        <div className="text-center py-6 bg-gray-50 rounded-lg">
                          <Clock className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                          <p className="text-gray-500 text-sm">No activity yet</p>
                          <p className="text-gray-400 text-xs mt-1">Events will appear as work progresses on this job</p>
                        </div>
                      );
                    }
                    
                    // Icon and label based on event type - exact copy from Handyman Portal
                    const getEventIcon = (type) => {
                      switch(type) {
                        case 'started_travel': return { icon: Navigation, color: 'text-blue-500', label: 'Started Travel' };
                        case 'started_job': return { icon: Play, color: 'text-green-500', label: 'Job Started' };
                        case 'paused_job': return { icon: Pause, color: 'text-orange-500', label: 'Job Paused' };
                        case 'resumed_job': return { icon: Play, color: 'text-teal-500', label: 'Job Resumed' };
                        case 'completed_job': return { icon: CheckCircle, color: 'text-green-600', label: 'Job Completed' };
                        case 'progress_update': return { icon: FileText, color: 'text-purple-500', label: 'Progress Update' };
                        case 'note_added': return { icon: FileText, color: 'text-gray-500', label: 'Note Added' };
                        case 'photo_uploaded': return { icon: Camera, color: 'text-pink-500', label: 'Photo Uploaded' };
                        case 'expense_added': return { icon: DollarSign, color: 'text-yellow-600', label: 'Expense Added' };
                        default: return { icon: Clock, color: 'text-gray-400', label: type?.replace(/_/g, ' ') || 'Event' };
                      }
                    };
                    
                    return (
                      <div className="space-y-3 max-h-80 overflow-y-auto">
                        {allEvents.map((event, idx) => {
                          const { icon: EventIcon, color, label } = getEventIcon(event.event_type);
                          
                          return (
                            <div key={event.id || idx} className="flex gap-3 p-3 bg-gray-50 rounded-lg">
                              <div className={`w-8 h-8 rounded-full bg-white border-2 flex items-center justify-center flex-shrink-0 ${color}`}>
                                <EventIcon className="w-4 h-4" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="font-medium text-sm text-gray-900">{label}</span>
                                  <span className="text-xs text-gray-400">
                                    {formatDateTime(event.timestamp)}
                                  </span>
                                </div>
                                <p className="text-xs text-gray-500">
                                  By {event.handyman_name || event.user_name || 'System'}
                                </p>
                                {(event.notes || event.work_completed) && (
                                  <p className="text-sm text-gray-700 mt-1 bg-white p-2 rounded border">
                                    {event.notes || event.work_completed}
                                  </p>
                                )}
                                {event.photos && event.photos.length > 0 && (
                                  <div className="flex gap-2 mt-2 flex-wrap">
                                    {event.photos.map((photo, pIdx) => (
                                      <a
                                        key={pIdx}
                                        href={photo}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="block w-12 h-12 rounded border overflow-hidden hover:opacity-80"
                                      >
                                        <img src={photo} alt={`Photo ${pIdx + 1}`} className="w-full h-full object-cover" />
                                      </a>
                                    ))}
                                  </div>
                                )}
                                {event.percentage_complete !== undefined && event.percentage_complete !== null && (
                                  <div className="mt-2">
                                    <div className="flex justify-between text-xs mb-1">
                                      <span>Progress</span>
                                      <span>{event.percentage_complete}%</span>
                                    </div>
                                    <div className="h-1.5 bg-gray-200 rounded-full">
                                      <div 
                                        className="h-full bg-gray-1000 rounded-full" 
                                        style={{width: `${event.percentage_complete}%`}}
                                      />
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                </CardContent>
              </Card>

              {/* Invoices Section */}
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <FileText className="w-5 h-5 text-[#595959]" />
                      Invoices
                    </CardTitle>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">{jobData.invoices?.length || 0} invoice(s)</Badge>
                      <Button size="sm" variant="outline" onClick={openCreateInvoiceDialog} disabled={creatingInvoice}>
                        {creatingInvoice ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
                        Create Invoice
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {jobData.invoices?.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b bg-gray-50">
                            <th className="text-left p-3 font-medium text-gray-600 w-8"></th>
                            <th className="text-left p-3 font-medium text-gray-600">Invoice #</th>
                            <th className="text-left p-3 font-medium text-gray-600">Type</th>
                            <th className="text-left p-3 font-medium text-gray-600">Date</th>
                            <th className="text-left p-3 font-medium text-gray-600">Due Date</th>
                            <th className="text-left p-3 font-medium text-gray-600">Status</th>
                            <th className="text-right p-3 font-medium text-gray-600">Amount</th>
                            <th className="text-center p-3 font-medium text-gray-600">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {jobData.invoices.map((inv, index) => (
                            <React.Fragment key={`inv-${index}`}>
                              <tr className="border-b hover:bg-gray-50 cursor-pointer" onClick={() => toggleExpand('invoice', index)}>
                                <td className="p-3">{expandedInvoices[index] ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}</td>
                                <td className="p-3 font-medium">{inv.invoice_number}</td>
                                <td className="p-3"><Badge variant="outline" className="capitalize">{inv.invoice_type || 'Full'}</Badge></td>
                                <td className="p-3">{formatDate(inv.created_at)}</td>
                                <td className="p-3">{formatDate(inv.due_date)}</td>
                                <td className="p-3"><Badge className={getStatusColor(inv.status)}>{inv.status}</Badge></td>
                                <td className="p-3 text-right font-medium">{formatCurrency(inv.total_amount)}</td>
                                <td className="p-3 text-center">
                                  <div className="flex items-center justify-center gap-1">
                                    <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); handleViewInvoice(inv); }} title="View Invoice">
                                      <Eye className="w-4 h-4" />
                                    </Button>
                                    {inv.status !== 'paid' && (
                                      <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); handleSendInvoice(inv.id); }} title="Send Invoice">
                                        <Send className="w-4 h-4" />
                                      </Button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                              {expandedInvoices[index] && (
                                <tr className="bg-gray-50">
                                  <td colSpan={8} className="p-4">
                                    <div className="ml-8 grid grid-cols-2 md:grid-cols-4 gap-4">
                                      <div><p className="text-xs text-gray-500">Subtotal</p><p className="font-medium">{formatCurrency(inv.subtotal)}</p></div>
                                      <div><p className="text-xs text-gray-500">Tax</p><p className="font-medium">{formatCurrency(inv.tax || 0)}</p></div>
                                      <div><p className="text-xs text-gray-500">Discount</p><p className="font-medium">{formatCurrency(inv.discount || 0)}</p></div>
                                      <div><p className="text-xs text-gray-500">Total</p><p className="font-bold text-[#595959]">{formatCurrency(inv.total_amount)}</p></div>
                                      {inv.notes && <div className="col-span-4"><p className="text-xs text-gray-500">Notes</p><p className="text-sm">{inv.notes}</p></div>}
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr className="bg-gray-50 font-medium">
                            <td colSpan={6} className="p-3 text-right">Total Invoiced</td>
                            <td className="p-3 text-right font-bold text-[#595959]">{formatCurrency(stats?.totalInvoiced)}</td>
                            <td></td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p>No invoices created yet.</p>
                      <Button size="sm" variant="outline" className="mt-3" onClick={openCreateInvoiceDialog} disabled={creatingInvoice}>
                        {creatingInvoice ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
                        Create Invoice
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Internal Notes Section */}
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <MessageSquare className="w-5 h-5 text-[#595959]" />
                      Internal Notes
                    </CardTitle>
                    <Button size="sm" variant="outline" onClick={() => setShowAddNote(true)}>
                      <Plus className="w-4 h-4 mr-1" /> Add Note
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {jobData.internalMessages?.length > 0 ? (
                    <div className="space-y-3">
                      {jobData.internalMessages.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)).map((msg, index) => (
                        <div key={index} className="p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-medium text-sm">{msg.author || 'Admin'}</span>
                            <span className="text-xs text-gray-500">{formatDateTime(msg.timestamp)}</span>
                          </div>
                          <p className="text-sm text-gray-700">{msg.message}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <MessageSquare className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p>No internal notes yet.</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          ) : (
            <div className="flex items-center justify-center h-96">
              <p className="text-gray-500">Job not found</p>
            </div>
          )}
        </div>

        {/* Add Expense Dialog */}
        <Dialog open={showAddExpense} onOpenChange={setShowAddExpense}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Expense</DialogTitle>
              <DialogDescription>Record an expense for this job</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div><Label>Item Name *</Label><Input value={newExpense.item} onChange={(e) => setNewExpense({ ...newExpense, item: e.target.value })} placeholder="e.g., Materials, Equipment" /></div>
              <div><Label>Category</Label>
                <Select value={newExpense.category} onValueChange={(v) => setNewExpense({ ...newExpense, category: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="materials">Materials</SelectItem>
                    <SelectItem value="equipment">Equipment</SelectItem>
                    <SelectItem value="travel">Travel</SelectItem>
                    <SelectItem value="subcontractor">Subcontractor</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Amount *</Label><Input type="number" min="0" step="0.01" value={newExpense.amount} onChange={(e) => setNewExpense({ ...newExpense, amount: parseFloat(e.target.value) || 0 })} /></div>
              <div><Label>Vendor</Label><Input value={newExpense.vendor} onChange={(e) => setNewExpense({ ...newExpense, vendor: e.target.value })} placeholder="e.g., Home Depot" /></div>
              <div><Label>Description</Label><Textarea value={newExpense.description} onChange={(e) => setNewExpense({ ...newExpense, description: e.target.value })} placeholder="Additional details..." /></div>
              <Button onClick={handleAddExpense} className="w-full bg-[#595959] hover:bg-[#404040]">Add Expense</Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Add Time Entry Dialog */}
        <Dialog open={showAddTimeEntry} onOpenChange={setShowAddTimeEntry}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Time Entry</DialogTitle>
              <DialogDescription>Log time worked on this job</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div><Label>Date</Label><Input type="date" value={newTimeEntry.date} onChange={(e) => setNewTimeEntry({ ...newTimeEntry, date: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Start Time</Label><Input type="time" value={newTimeEntry.start_time} onChange={(e) => setNewTimeEntry({ ...newTimeEntry, start_time: e.target.value })} /></div>
                <div><Label>End Time</Label><Input type="time" value={newTimeEntry.end_time} onChange={(e) => setNewTimeEntry({ ...newTimeEntry, end_time: e.target.value })} /></div>
              </div>
              <div><Label>Notes</Label><Textarea value={newTimeEntry.notes} onChange={(e) => setNewTimeEntry({ ...newTimeEntry, notes: e.target.value })} placeholder="Work performed..." /></div>
              <Button onClick={handleAddTimeEntry} className="w-full bg-[#595959] hover:bg-[#404040]">Add Time Entry</Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Add Note Dialog */}
        <Dialog open={showAddNote} onOpenChange={setShowAddNote}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Internal Note</DialogTitle>
              <DialogDescription>This note will only be visible to your team.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <Textarea value={newNote} onChange={(e) => setNewNote(e.target.value)} placeholder="Enter your note..." rows={4} />
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setShowAddNote(false)} className="flex-1">Cancel</Button>
                <Button onClick={handleAddNote} className="flex-1 bg-[#595959] hover:bg-[#404040]">Add Note</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Professional Invoice View - Unified Template */}
        {showInvoiceView && selectedInvoice && (
          <InvoiceView
            key={`invoice-${selectedInvoice?.id}`}
            open={showInvoiceView}
            onOpenChange={(open) => {
              setShowInvoiceView(open);
              if (!open) {
                setSelectedInvoice(null);
                fetchJobDetails();
              }
            }}
            invoiceId={selectedInvoice?.id}
            onRefresh={fetchJobDetails}
            editable={selectedInvoice?.status !== 'paid'}
          />
        )}

        {/* Pricing Calculator Dialog */}
        <Dialog open={showPricingCalculator} onOpenChange={setShowPricingCalculator}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Pricing Calculator</DialogTitle>
              <DialogDescription>Calculate unit price from cost and markup</DialogDescription>
            </DialogHeader>
            {pricingCalcIndex !== null && editableItems[pricingCalcIndex] && (
              <div className="space-y-4">
                <div><Label>Unit Cost ($)</Label><Input type="number" min="0" value={editableItems[pricingCalcIndex].unit_cost} onChange={(e) => updateEditableItem(pricingCalcIndex, 'unit_cost', parseFloat(e.target.value) || 0)} /><p className="text-xs text-gray-500 mt-1">Internal only - customer will not see this</p></div>
                <div><Label>Markup (%)</Label><Input type="number" min="0" max="500" value={editableItems[pricingCalcIndex].markup_percent} onChange={(e) => updateEditableItem(pricingCalcIndex, 'markup_percent', parseFloat(e.target.value) || 0)} /></div>
                <div className="p-4 bg-gray-100 rounded-lg">
                  <div className="flex justify-between text-sm mb-2"><span>Unit Cost:</span><span>{formatCurrency(editableItems[pricingCalcIndex].unit_cost)}</span></div>
                  <div className="flex justify-between text-sm mb-2"><span>Markup ({editableItems[pricingCalcIndex].markup_percent}%):</span><span>{formatCurrency(editableItems[pricingCalcIndex].unit_cost * editableItems[pricingCalcIndex].markup_percent / 100)}</span></div>
                  <hr className="my-2" />
                  <div className="flex justify-between font-bold text-[#595959]"><span>Unit Price:</span><span>{formatCurrency(editableItems[pricingCalcIndex].unit_price)}</span></div>
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setShowPricingCalculator(false)}>Close</Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Assign Handyman Dialog */}
        <Dialog open={showAssignHandyman} onOpenChange={setShowAssignHandyman}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <UserCheck className="w-5 h-5 text-[#595959]" />
                Assign Handyman
              </DialogTitle>
              <DialogDescription>Select a handyman to assign to this job</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Select Handyman</Label>
                <Select value={selectedHandymanId} onValueChange={setSelectedHandymanId}>
                  <SelectTrigger data-testid="handyman-select">
                    <SelectValue placeholder="Choose a handyman..." />
                  </SelectTrigger>
                  <SelectContent>
                    {handymenList.map((h) => (
                      <SelectItem key={h.id} value={h.id}>
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4" />
                          {h.name}
                          {h.skills && h.skills.length > 0 && (
                            <span className="text-xs text-gray-500">({h.skills.slice(0, 2).join(', ')})</span>
                          )}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {selectedHandymanId && handymenList.find(h => h.id === selectedHandymanId) && (
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm font-medium">{handymenList.find(h => h.id === selectedHandymanId)?.name}</p>
                  <p className="text-xs text-gray-500">{handymenList.find(h => h.id === selectedHandymanId)?.email}</p>
                  <p className="text-xs text-gray-500">{handymenList.find(h => h.id === selectedHandymanId)?.phone}</p>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowAssignHandyman(false)}>Cancel</Button>
              <Button 
                className="bg-[#595959] hover:bg-[#404040]" 
                onClick={handleAssignHandyman}
                disabled={assigningHandyman || !selectedHandymanId}
                data-testid="confirm-assign-handyman"
              >
                {assigningHandyman ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Assigning...
                  </>
                ) : (
                  <>
                    <UserCheck className="w-4 h-4 mr-2" />
                    Assign Handyman
                  </>
                )}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Create Invoice Dialog - with Advance Amount */}
        <Dialog open={showCreateInvoiceDialog} onOpenChange={setShowCreateInvoiceDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#595959]" />
                Create Invoice
              </DialogTitle>
              <DialogDescription>
                Configure invoice settings before creating
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {/* Show actual payments received */}
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center gap-2 mb-2">
                  <CreditCard className="w-4 h-4 text-blue-600" />
                  <span className="text-sm font-medium text-blue-800">Payments Received</span>
                </div>
                <p className="text-lg font-bold text-blue-900">{formatCurrency(actualPayments)}</p>
                <p className="text-xs text-blue-600 mt-1">
                  Total payments from Payment Section for this customer/job
                </p>
              </div>
              
              {/* Advance Amount Input */}
              <div className="space-y-2">
                <Label htmlFor="advance-amount" className="flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-gray-500" />
                  Advance/Prepaid Amount to Deduct
                </Label>
                <Input
                  id="advance-amount"
                  type="number"
                  min="0"
                  step="0.01"
                  value={advanceAmount}
                  onChange={(e) => setAdvanceAmount(parseFloat(e.target.value) || 0)}
                  placeholder="Enter advance amount"
                  data-testid="advance-amount-input"
                />
                <p className="text-xs text-gray-500">
                  This amount will be shown as Advance Paid and deducted from the total
                </p>
              </div>
              
              {/* Quick options */}
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setAdvanceAmount(actualPayments)}
                  className="text-xs"
                >
                  Use Actual Payments ({formatCurrency(actualPayments)})
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setAdvanceAmount(0)}
                  className="text-xs"
                >
                  No Advance
                </Button>
              </div>
            </div>
            
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => setShowCreateInvoiceDialog(false)}>
                Cancel
              </Button>
              <Button 
                className="bg-[#595959] hover:bg-[#404040]" 
                onClick={handleCreateInvoice}
                disabled={creatingInvoice}
                data-testid="confirm-create-invoice"
              >
                {creatingInvoice ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <FileText className="w-4 h-4 mr-2" />
                    Create Invoice
                  </>
                )}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
}
